import React, { useEffect, useState } from 'react';

import styled from 'styled-components';

import HangarIcon from '~/icons/components/HangarIcon';
import { mobile, SkeletonLine } from '~/techstyle-shared/react-components';

import useFadeOutUnmount from '../../../hooks/useFadeOutUnmount';

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  border-radius: 4px;
`;

const StyledSkeletonLine = styled(SkeletonLine)<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 0.3s;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

const StyledImage = styled.img<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background-color: ${({ theme }) => theme.colors.gridV2FilterBtnBgHover};
  transition: opacity 0.3s;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

const ErrorOverlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.gridV2FilterBtnBgHover};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.3s;

  /* Center the icon within the card, accounting for padding. */
  padding-bottom: 36px;
  ${mobile`
    padding-bottom: 33px;
  `}
`;

type FilterImageProps = {
  alt?: string;
  isFetchingAssets?: boolean;
  src?: string;
};

const FilterImage = ({
  src,
  alt,
  isFetchingAssets = true,
}: FilterImageProps) => {
  const [hasImageLoaded, setHasImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  // Reset image loaded state when a new image is fetched.
  useEffect(() => {
    setHasImageLoaded(false);
    setHasImageError(false);
  }, [src, isFetchingAssets]);

  const hasValidSrc = typeof src === 'string' && src.trim().length > 0;
  const imageSrcToRender = !isFetchingAssets && hasValidSrc ? src : undefined;
  const shouldShowErrorOverlay =
    !isFetchingAssets && (!hasValidSrc || hasImageError);

  // Keep skeleton visible during fetch and until the image has actually loaded.
  const shouldRenderImage = Boolean(imageSrcToRender);
  const isSkeletonVisible =
    isFetchingAssets || (!hasImageLoaded && !shouldShowErrorOverlay);

  const { isMounted: showSkeleton, onTransitionEnd: onSkeletonTransitionEnd } =
    useFadeOutUnmount(isSkeletonVisible);

  const handleImageError = () => {
    setHasImageError(true);
  };

  const handleImageLoad = () => {
    // onLoad marks the handoff point where image fades in and skeleton fades out.
    setHasImageLoaded(true);
  };

  return (
    <ImageContainer aria-busy={isFetchingAssets}>
      {showSkeleton && (
        <StyledSkeletonLine
          $isVisible={isSkeletonVisible}
          onTransitionEnd={onSkeletonTransitionEnd}
        />
      )}
      {/* Image is not mounted while fetching, so onLoad/onError cannot fire prematurely. */}
      {shouldRenderImage && (
        <StyledImage
          $isVisible={hasImageLoaded && !hasImageError}
          src={imageSrcToRender}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}
      <ErrorOverlay $isVisible={shouldShowErrorOverlay}>
        <HangarIcon />
      </ErrorOverlay>
    </ImageContainer>
  );
};

export default FilterImage;
